import { z } from 'zod'

/**
 * MoneyMitra's blog posts: Home's "Latest from MoneyMitra" row and the in-app article page.
 *
 * Wire shapes are `PostCard` / `PostDetail` in `@/api/schema`; this is the app's side, after
 * `postsService`'s mappers. `slug` and `category_type` are left out: no route takes the slug, and
 * nothing draws the category type.
 */
export const postCardSchema = z.object({
  /** The route key (`/posts/$postId`). */
  id: z.number().int().positive(),
  title: z.string().min(1),
  summary: z.string(),
  /** 16:9 on `cdn.moneymitra.com/media/`. Null when the post has no featured image. */
  imageUrl: z.url().nullable(),
  /** The category's title, the card's label ("Personal Finance"). */
  category: z.string(),
  /** 0 when the CMS kept no estimate; the meta line then leaves it out. */
  readTimeMinutes: z.number().int().nonnegative(),
  /** ISO with the +05:45 offset. A string, so a browser in another zone never moves the day. */
  publishedAt: z.string().min(1),
  isPremium: z.boolean(),
})

/**
 * What the article page draws under the header. One of three, so a locked post can never also
 * carry a body: mm-be sends `content: null` with `locked: true`, and an unlocked post whose
 * content is null or blank has nothing to read beyond its summary.
 */
export const postBodySchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('locked') }),
  z.object({ kind: z.literal('empty') }),
  /** Cleaned on mm-be, and drawn only through `SafeHtml` all the same. */
  z.object({ kind: z.literal('html'), html: z.string().min(1) }),
])

export const postDetailSchema = postCardSchema.extend({
  tags: z.array(z.string().min(1)),
  body: postBodySchema,
})

export type PostCard = z.infer<typeof postCardSchema>
export type PostBody = z.infer<typeof postBodySchema>
export type PostDetail = z.infer<typeof postDetailSchema>
